// What an older bundle left in a till's storage, read by this one.
//
// Issue #123 renamed one key that a cart and the body of an unsent sale carry:
// the id of the delivery area. A till may reload onto this bundle with either
// record still stored under the name it had before, so both are read through
// here. The key takes its new name, keeps its value and keeps its place among
// the others, so a sale sent again differs from what was stored in that one
// name only. The API takes only the current name, and it fingerprints a sale
// under the text it has always used, so a retry sent under the current name
// still matches the key it was first sent under
// (internal/core/usecase/uc05_place_order.go).
//
// This file is the only place in the bundle that writes the old name. The
// stored-format compat test imports it from here (draft/compat.test.ts).

/** The one key a stored record may carry under an earlier name. */
export const renamedKey = { from: 'deliveryZoneId', to: 'deliveryAreaId' } as const;

/**
 * A stored object read under the current name: a copy with the renamed key
 * moved to its new name, in the same place, or the object itself when it
 * holds no old name. A record never holds both names, because one bundle wrote
 * it; if one did, the current name wins, since the API takes no other.
 */
export function readStoredKeys<T>(record: T): T {
	if (record === null || typeof record !== 'object' || Array.isArray(record)) return record;
	if (!Object.hasOwn(record, renamedKey.from)) return record;
	const hasCurrent = Object.hasOwn(record, renamedKey.to);
	const read: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(record)) {
		if (key !== renamedKey.from) read[key] = value;
		else if (!hasCurrent) read[renamedKey.to] = value;
	}
	return read as T;
}

/** A cart as this bundle reads it, whichever bundle stored it. */
export function readStoredDraft<T>(saved: T | null): T | null {
	return readStoredKeys(saved);
}

/** An unsent sale as this bundle sends it again: its body under the current names. */
export function readStoredPending<T extends { body: unknown }>(saved: T | null): T | null {
	if (saved === null || typeof saved !== 'object') return saved;
	const body = readStoredKeys(saved.body);
	return body === saved.body ? saved : { ...saved, body };
}
